import type { JSX } from 'react';
import type { CapChangeResource } from '@voxler/contracts';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { capLabel, formatDateTime } from './client-helpers';
import { internalFetch } from '../lib/api';
import { Card, CardHead } from '@/components/ui/card';
import { Pager, usePagination } from '@/components/ui/pager';
import { Tag } from '@/components/ui/tag';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const SCOPE_LABELS: Record<CapChangeResource['scope'], string> = {
  user: 'Utilisateur',
  client: 'Client',
  safety: 'Sécurité',
  platform: 'Plateforme',
};

const ACTOR_LABELS: Record<CapChangeResource['actorType'], string> = {
  operator: 'Opérateur',
  admin_key: 'Clé admin',
  end_user: 'Utilisateur',
};

const COLUMN_COUNT = 5;

export function ClientCapHistory({ clientId }: { clientId: string }): JSX.Element {
  const history = useQuery({
    queryKey: ['cap-changes', clientId],
    queryFn: () =>
      internalFetch<{ changes: CapChangeResource[] }>(`/internal/clients/${clientId}/cap-changes`),
  });

  const rows = history.data?.changes ?? [];
  const pagination = usePagination(rows, 25);

  return (
    <Card>
      <CardHead
        title="Historique des plafonds"
        description="Toute modification de plafond (client, utilisateur, sécurité), avec son auteur et sa date. Conservé pour les litiges."
        className="pb-4"
      />
      <Table className="min-w-[720px]">
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Plafond</TableHead>
            <TableHead>Utilisateur</TableHead>
            <TableHead>Changement</TableHead>
            <TableHead>Auteur</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagination.pageItems.map((change) => (
            <TableRow key={change.id}>
              <TableCell kind="mono" className="whitespace-nowrap">
                {formatDateTime(change.createdAt)}
              </TableCell>
              <TableCell>
                <Tag>{SCOPE_LABELS[change.scope]}</Tag>
              </TableCell>
              <TableCell kind={change.endUserExternalId === null ? 'sub' : 'mono'}>
                {change.endUserExternalId ?? '—'}
              </TableCell>
              <TableCell className="whitespace-nowrap font-mono tabular-nums">
                <span className="inline-flex items-center gap-2">
                  <span className="text-muted-foreground">{capLabel(change.previousCap)}</span>
                  <ArrowRight aria-hidden="true" className="size-3.5 text-muted-foreground" />
                  <span className="sr-only">devient</span>
                  <span className="font-medium">{capLabel(change.newCap)}</span>
                </span>
              </TableCell>
              <TableCell>
                <Cell2 primary={ACTOR_LABELS[change.actorType]} secondary={change.actorLabel} />
              </TableCell>
            </TableRow>
          ))}
          {history.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
          ) : null}
          {rows.length === 0 && !history.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>
              Aucune modification de plafond enregistrée.
            </TableEmptyRow>
          ) : null}
        </TableBody>
      </Table>
      <Pager itemLabel="modifications" {...pagination.pagerProps} />
    </Card>
  );
}
